import React from 'react';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SelectionPanelGroupTheme } from '../common/SelectionPanelGroupTheme';
import { SelectionPanelBorderRadius } from '../common/SelectionPanelBorderRadius';
import { SelectionPanelDropShadowType } from '../common/SelectionPanelDropShadowType';
import { SelectionPanelGroupStyleMode } from '../common/SelectionPanelGroupStyleMode';
import { SelectionPanelCheckShape } from '../common/SelectionPanelCheckShape';
import { SelectionPanelCheckStyle } from '../common/SelectionPanelCheckStyle';
import { SelectionPanelGroupSelectionType } from '../common/SelectionPanelGroupSelectionType';

export const SelectionPanel: React.FC<{
    id: string;
    children: any;
    selectedPanels?: string[]; //Should be passed into parent group control
    set_selectedPanels?: (panelNumber: string[]) => void; //Should be passed into parent group control
    selectionType?: SelectionPanelGroupSelectionType; //Should be passed into parent group control
    maxSelection?: number; //Should be passed into parent group control
    borderRadius?: SelectionPanelBorderRadius; //Should be passed into parent group control
    dropShadowType?: SelectionPanelDropShadowType; //Should be passed into parent group control
    wrapPanels?: boolean; //Should be passed into the parent group control
    panelWidth?: string; //Should be passed into the parent group control - only applicable for wrapPanels
    handleClick?: React.MouseEventHandler;
    enabled?: boolean;
    theme?: SelectionPanelGroupTheme;
    styleMode?: SelectionPanelGroupStyleMode; //Should be passed into the parent group control
    checkShape?: SelectionPanelCheckShape; //Should be passed into the parent group control - omit for the style mode's default
    checkStyle?: SelectionPanelCheckStyle; //Should be passed into the parent group control
}> = ({
    id,
    children,
    selectionType = SelectionPanelGroupSelectionType.Single,
    selectedPanels,
    set_selectedPanels,
    maxSelection,
    borderRadius = SelectionPanelBorderRadius.None,
    dropShadowType = SelectionPanelDropShadowType.None,
    wrapPanels = false,
    panelWidth = '100%',
    handleClick,
    enabled = true,
    theme = SelectionPanelGroupTheme.Blue,
    styleMode = SelectionPanelGroupStyleMode.Enhanced,
    checkShape,
    checkStyle = SelectionPanelCheckStyle.Checkmark,
}) => {
    const isFill = checkStyle === SelectionPanelCheckStyle.Fill;

    const setStyle = {
        cursor: `${enabled ? 'pointer' : 'arrow'}`,
        maxWidth: `${wrapPanels ? panelWidth : '100%'}`,
    };

    const performClick = (e: React.MouseEvent) => {
        if (enabled) {
            //Do Update of selection
            updateSelections(e);

            //Perform any additional selection click action passed to the control
            if (handleClick) {
                handleClick(e);
            }
        }
    };

    const updateSelections = (e: React.MouseEvent) => {
        if (e.currentTarget != undefined) {
            const currentId = e.currentTarget.id;
            if (set_selectedPanels != undefined) {
                if (selectionType === SelectionPanelGroupSelectionType.Single) {
                    set_selectedPanels([currentId]);
                } else {
                    // Work out the new list here and pass an array (not an updater function), so set_selectedPanels can
                    // be any callback, not only a React state setter.
                    const current = selectedPanels ?? [];
                    if (!maxSelection || current.length <= maxSelection) {
                        if (current.includes(currentId)) {
                            // Deselect
                            set_selectedPanels(current.filter((item) => item !== currentId));
                        } else if (!maxSelection || current.length < maxSelection) {
                            // Select
                            set_selectedPanels([...current, currentId]);
                        }
                        // else: already at maxSelection, so the click is ignored (as before)
                    }
                }
            }
        }
    };

    return (
        <>
            <div
                id={id}
                className={`selectionPanel ${theme} ${borderRadius} ${dropShadowType} ${wrapPanels ? 'wrapped' : 'unwrapped'} ${selectedPanels?.some((panel) => panel === id) ? 'selected' : ''}${styleMode === SelectionPanelGroupStyleMode.Condensed ? ' condensed' : ''}`}
                key={`selectionPanel_${id}`}
                onClick={performClick}
                style={setStyle}
            >
                <div className='selectionPanel-content'>
                    <div
                        className={`selectionPanel-button ${theme} ${selectedPanels?.some((panel) => panel === id) ? 'selected' : ''}${checkShape ? ` ${checkShape}` : ''}${isFill ? ' fill' : ''}`}
                    >
                        {!isFill && selectedPanels?.some((panel) => panel === id) && <FontAwesomeIcon icon={faCheck} />}
                    </div>
                    <div className='selectionPanel-children'>{children}</div>
                </div>
            </div>
        </>
    );
};
